import { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import type { FilterData } from './types';
import { filterFormSchema, type FilterFormValues } from './schema';

interface UseFilterFormParams {
  isOpen: boolean;
  editFilter?: (FilterData & { index: number }) | null;
  onSubmit: (filter: FilterData) => void;
  onClose: () => void;
}

export const useFilterForm = ({ isOpen, editFilter, onSubmit, onClose }: UseFilterFormParams) => {
  const form = useForm<FilterFormValues>({
    resolver: valibotResolver(filterFormSchema),
    defaultValues: {
      filterType: 'instance',
      instanceText: '',
      selectedPims: [],
    },
  });

  const {
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = form;
  const filterType = watch('filterType');

  useEffect(() => {
    if (!isOpen) return;

    if (editFilter) {
      reset({
        filterType: editFilter.filterType as 'instance' | 'pims',
        instanceText: editFilter.filterType === 'instance' ? editFilter.filterData.join(',\n') : '',
        selectedPims: editFilter.filterType === 'pims' ? [...editFilter.filterData] : [],
      });
    } else {
      reset({
        filterType: 'instance',
        instanceText: '',
        selectedPims: [],
      });
    }
  }, [isOpen, editFilter, reset]);

  const onFormSubmit = useCallback(
    (data: FilterFormValues) => {
      if (data.filterType === 'instance') {
        const ids = (data.instanceText ?? '')
          .split(',')
          .map(s => s.trim().toLowerCase())
          .filter(Boolean);
        onSubmit({ filterType: 'instance', filterData: ids });
      } else {
        onSubmit({ filterType: 'pims', filterData: data.selectedPims ?? [] });
      }
      onClose();
    },
    [onSubmit, onClose],
  );

  return {
    form,
    filterType,
    errors,
    handleSubmit: handleSubmit(onFormSubmit),
  };
};
